import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Button,
  FormControl,
  InputLabel,
  Select,
  Typography,
} from '@material-ui/core';
import { MaterialReactTable, MRT_ColumnDef } from 'material-react-table';
import { useHistory, useLocation, useParams } from 'react-router-dom';
import PageTitle from '../../ui/PageTitle';
import { useButtonsStyles } from '../../../styles/useButtonsStyles';
import { useListingsFiltrationStyles } from '../../../styles/useListingsFiltrationStyles';
import { useYardiSyncExceptionsQuery, useYardiSyncPropertiesQuery } from '../../../queries/yardiSync';
import { YardiSyncException } from '../../../types/yardiSync';
import { formatDateTimeToLocal } from '../../../formatters/dateFormatters/dateFormatters';
import { PRINT_HYPHEN } from '../../../constants';

const EXCEPTIONS_LIMIT = 200;

const getErrorMessage = (error: unknown, fallback: string): string => {
  const responseData = (error as { response?: { data?: unknown } })?.response?.data;
  if (typeof responseData === 'string') {
    return responseData;
  }
  if (responseData && typeof responseData === 'object') {
    const data = responseData as { error?: string; message?: string };
    if (data.error) {
      return data.error;
    }
    if (data.message) {
      return data.message;
    }
  }
  const message = (error as Error)?.message;
  return message || fallback;
};

const YardiSyncExceptions: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const runId = Number(id);
  const history = useHistory();
  const location = useLocation();
  const buttonClasses = useButtonsStyles();
  const filterClasses = useListingsFiltrationStyles();
  const query = new URLSearchParams(location.search);

  const [propertyCode, setPropertyCode] = useState<string>(query.get('propertyCode') || '');
  const [offset, setOffset] = useState(0);
  const [exceptionsList, setExceptionsList] = useState<YardiSyncException[]>([]);

  const { data: propertiesData } = useYardiSyncPropertiesQuery();
  const {
    data: exceptionsData,
    isLoading,
    isFetching,
    isError,
    error,
  } = useYardiSyncExceptionsQuery(runId, EXCEPTIONS_LIMIT, offset, propertyCode || undefined);

  useEffect(() => {
    query.set('propertyCode', propertyCode);
    history.replace({
      pathname: location.pathname,
      search: `?${query.toString()}`,
    });
    setOffset(0);
    setExceptionsList([]);
  }, [propertyCode]);

  useEffect(() => {
    if (!exceptionsData) {
      return;
    }
    setExceptionsList((prev) => (offset === 0 ? exceptionsData : [...prev, ...exceptionsData]));
  }, [exceptionsData, offset]);

  const columns = useMemo<MRT_ColumnDef<YardiSyncException>[]>(() => [
    {
      header: 'Type',
      accessorKey: 'exceptionType',
      enableSorting: false,
    },
    {
      header: 'Severity',
      accessorKey: 'severity',
      enableSorting: false,
    },
    {
      header: 'Property',
      accessorKey: 'propertyCode',
      enableSorting: false,
    },
    {
      header: 'Row Index',
      accessorKey: 'rowIndex',
      enableSorting: false,
      size: 80,
    },
    {
      header: 'Stage Table',
      accessorKey: 'stageTable',
      enableSorting: false,
    },
    {
      header: 'Message',
      accessorKey: 'message',
      enableSorting: false,
    },
    {
      header: 'Created At',
      accessorKey: 'createdAt',
      // eslint-disable-next-line react/display-name
      accessorFn: (row) => (row.createdAt ? formatDateTimeToLocal(row.createdAt) : PRINT_HYPHEN),
      enableSorting: false,
      size: 160,
    },
  ], []);

  const hasMore = exceptionsData ? exceptionsData.length === EXCEPTIONS_LIMIT : false;

  return (
    <>
      <Box display="flex" justifyContent="space-between" alignItems="flex-end" mb={3}>
        <PageTitle>{`Run ${runId} Exceptions`}</PageTitle>
      </Box>
      <Box display="flex" alignItems="center" mb={2} className={filterClasses.listingFilterWrapper}>
        <Box mr={2} minWidth={200}>
          <FormControl fullWidth>
            <InputLabel shrink={!!propertyCode} htmlFor="filter-property-code">Property Code</InputLabel>
            <Select
              native
              value={propertyCode}
              onChange={(event) => setPropertyCode(String(event.target.value))}
              label="Property Code"
              inputProps={{
                name: 'filter-property-code',
                id: 'filter-property-code',
              }}
            >
              <option aria-label="None" value="" />
              {(propertiesData || []).map((property) => (
                <option key={property.id} value={property.propertyCode}>
                  {property.propertyCode}
                </option>
              ))}
            </Select>
          </FormControl>
        </Box>
        <Button
          className={buttonClasses.standard}
          variant="outlined"
          onClick={() => history.push(`/yardi/runs/${runId}`)}
        >
          Back to Run
        </Button>
      </Box>
      {isError ? (
        <Typography color="error">
          {getErrorMessage(error, 'Unable to load exceptions.')}
        </Typography>
      ) : (
        <MaterialReactTable
          columns={columns}
          data={exceptionsList}
          enableTopToolbar={false}
          enableColumnResizing
          enableColumnActions={false}
          enableFullScreenToggle={false}
          enableRowOrdering={false}
          enableSorting={false}
          enableGlobalFilterModes={false}
          enableDensityToggle={false}
          state={{ isLoading: isLoading || isFetching }}
          layoutMode="grid"
          muiTableProps={{
            sx: {
              tableLayout: 'fixed',
              display: 'table',
            },
          }}
        />
      )}
      {hasMore && (
        <Box display="flex" justifyContent="center" mt={2}>
          <Button
            className={buttonClasses.standard}
            variant="outlined"
            color="primary"
            disabled={isFetching}
            onClick={() => setOffset((prev) => prev + EXCEPTIONS_LIMIT)}
          >
            Load more
          </Button>
        </Box>
      )}
    </>
  );
};

export default YardiSyncExceptions;
